import { useEffect, useRef, useState } from 'react';

/**
 * useMinDuration — force l'affichage d'un state pendant `minMs` minimum
 * avant de laisser passer la mise à jour suivante.
 *
 * Cas d'usage (TurnIndicator) : `opponent-thinking → user-turn` peut
 * s'enchaîner en < 16ms (coup adversaire scripté rejoué dans le même
 * `requestAnimationFrame`). Sans ce délai, Léon ne voit jamais le state
 * intermédiaire et conserve l'impression que rien ne se passe.
 *
 * Comportement
 * ------------
 * - Première valeur : appliquée immédiatement.
 * - Transition < `minMs` après le dernier changement appliqué : retardée
 *   jusqu'à expiration du délai. Si plusieurs valeurs arrivent pendant
 *   l'attente, c'est toujours la plus récente qui est appliquée.
 * - Transition espacée de plus de `minMs` : appliquée immédiatement.
 *
 * Hook isolé dans son propre fichier pour éviter le warning
 * `react-refresh/only-export-components` lorsque exporté à côté d'un
 * composant React.
 *
 * @param value valeur courante côté caller (peut changer souvent)
 * @param minMs durée minimum d'affichage de chaque valeur (ms)
 * @returns valeur "lissée" à utiliser dans le render
 */
export function useMinDuration<T>(value: T, minMs: number): T {
  const [displayed, setDisplayed] = useState<T>(value);
  const lastChangeRef = useRef<number>(typeof performance !== 'undefined' ? performance.now() : 0);
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (Object.is(value, displayed)) {
      return undefined;
    }
    const now = typeof performance !== 'undefined' ? performance.now() : 0;
    const elapsed = now - lastChangeRef.current;
    const remaining = Math.max(0, minMs - elapsed);
    if (pendingRef.current) {
      clearTimeout(pendingRef.current);
      pendingRef.current = null;
    }
    if (remaining === 0) {
      lastChangeRef.current = now;
      setDisplayed(value);
      return undefined;
    }
    pendingRef.current = setTimeout(() => {
      lastChangeRef.current = typeof performance !== 'undefined' ? performance.now() : 0;
      setDisplayed(value);
      pendingRef.current = null;
    }, remaining);
    return () => {
      if (pendingRef.current) {
        clearTimeout(pendingRef.current);
        pendingRef.current = null;
      }
    };
  }, [value, displayed, minMs]);

  return displayed;
}
